/**
 * ドメインリスト管理サービス
 * システムテンプレートとユーザー作成リストの管理
 */
import type { ApiResponse, DomainList } from '../types';
import { apiRequest } from '../utils/api';

/**
 * ドメインリスト作成リクエスト
 */
export interface CreateDomainListRequest {
  name: string;
  description: string;
  domains: string[];
}

/**
 * ドメインリスト更新リクエスト
 */
export interface UpdateDomainListRequest {
  name?: string;
  description?: string;
  domains?: string[];
}

/**
 * ドメインリスト一覧を取得（システムテンプレート + ユーザー作成）
 */
export const getDomainLists = async (): Promise<ApiResponse<DomainList[]>> => {
  try {
    const response = await apiRequest('/api/domain-lists', {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ドメインリスト取得エラー:', errorText);
      throw new Error(`ドメインリストの取得に失敗しました: ${response.status}`);
    }

    const result = await response.json();
    return {
      success: true,
      data: result.data || [],
    };
  } catch (error) {
    console.error('Get domain lists failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'ドメインリストの取得に失敗しました',
    };
  }
};

/**
 * 新しいドメインリストを作成
 */
export const createDomainList = async (
  request: CreateDomainListRequest,
): Promise<ApiResponse<DomainList>> => {
  try {
    const response = await apiRequest('/api/domain-lists', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ドメインリスト作成エラー:', errorText);
      throw new Error(`ドメインリストの作成に失敗しました: ${response.status}`);
    }

    const result = await response.json();
    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error('Create domain list failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'ドメインリストの作成に失敗しました',
    };
  }
};

/**
 * ドメインリストを更新
 */
export const updateDomainList = async (
  listId: string,
  request: UpdateDomainListRequest,
): Promise<ApiResponse<DomainList>> => {
  try {
    const response = await apiRequest(`/api/domain-lists/${listId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ドメインリスト更新エラー:', errorText);
      throw new Error(`ドメインリストの更新に失敗しました: ${response.status}`);
    }

    const result = await response.json();
    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error('Update domain list failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'ドメインリストの更新に失敗しました',
    };
  }
};

/**
 * ドメインリストを削除
 */
export const deleteDomainList = async (listId: string): Promise<ApiResponse<void>> => {
  try {
    const response = await apiRequest(`/api/domain-lists/${listId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ドメインリスト削除エラー:', errorText);
      throw new Error(`ドメインリストの削除に失敗しました: ${response.status}`);
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Delete domain list failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'ドメインリストの削除に失敗しました',
    };
  }
};

/**
 * ドメインのバリデーション
 */
export const validateDomain = (domain: string): boolean => {
  // 基本的なドメイン形式チェック（ワイルドカード対応）
  const domainPattern =
    /^(\*\.)?[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/;
  return domainPattern.test(domain);
};

/**
 * ドメインリストのテキストをパース
 */
export const parseDomainsText = (text: string): string[] => {
  return text
    .split(/[\n,]/)
    .map((d) => d.trim())
    .filter((d) => d.length > 0);
};

/**
 * ドメインリストをテキストに変換
 */
export const domainsToText = (domains: string[]): string => {
  return domains.join('\n');
};
